import { useState, useCallback } from 'react';
import { AiReportDataset, AiReportDatasetRow } from '../models/AiReportDataset';
import aiReportDatasetService, { AiReportDatasetInput } from '../services/aiReportDatasetService';

export const useAiReportDatasets = () => {
    const [datasets, setDatasets] = useState<AiReportDataset[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchDatasets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await aiReportDatasetService.getAll();
            setDatasets(data);
        } catch (err) {
            setError('Failed to load data sets');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchDatasetById = async (id: number) => {
        setLoading(true);
        setError(null);
        try {
            return await aiReportDatasetService.getById(id);
        } catch (err) {
            setError(`Failed to load data set ${id}`);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const addDataset = async (data: AiReportDatasetInput) => {
        const created = await aiReportDatasetService.create(data);
        setDatasets((prev) => [...prev, created]);
        return created;
    };

    const updateDataset = async (id: number, data: AiReportDatasetInput) => {
        const updated = await aiReportDatasetService.update(id, data);
        setDatasets((prev) => prev.map((d) => (d.id === id ? updated : d)));
        return updated;
    };

    const deleteDataset = async (id: number) => {
        await aiReportDatasetService.remove(id);
        setDatasets((prev) => prev.filter((d) => d.id !== id));
    };

    const addRow = (datasetId: number, rowData: Record<string, string>): Promise<AiReportDatasetRow> =>
        aiReportDatasetService.addRow(datasetId, rowData);

    const updateRow = (datasetId: number, rowId: number, rowData: Record<string, string>): Promise<AiReportDatasetRow> =>
        aiReportDatasetService.updateRow(datasetId, rowId, rowData);

    const deleteRow = (datasetId: number, rowId: number): Promise<void> =>
        aiReportDatasetService.removeRow(datasetId, rowId);

    const importRows = (datasetId: number, file: File) =>
        aiReportDatasetService.bulkImport(datasetId, file);

    const bulkAddRows = (datasetId: number, rows: Record<string, string>[]) =>
        aiReportDatasetService.bulkAddRows(datasetId, rows);

    return {
        datasets,
        loading,
        error,
        fetchDatasets,
        fetchDatasetById,
        addDataset,
        updateDataset,
        deleteDataset,
        addRow,
        updateRow,
        deleteRow,
        importRows,
        bulkAddRows,
    };
};
